/**
 * CompanyPanel — Settings → COMPANY tab. Knit-style company settings:
 * LOCATIONS table (description/address/city/province/postal/phone, primary
 * flag, add/edit/delete) + COMPANY DETAILS (legal name, CRA BN, payroll
 * reference, ROE contact). Employee "Office" work locations resolve their
 * address from the primary location here.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Building2, MapPin, Pencil, Plus, Trash2, X } from "lucide-react";
import { PROVINCES, LANGUAGES } from "@/components/dashboard/payroll/knitData";

const inputCls = "mt-1 border-white/15 bg-white/5 text-white";

const emptyLoc = { location_id: "", description: "", address: "", city: "", province: "Ontario", postal: "", phone: "", primary: false };

export default function CompanyPanel() {
  const [profile, setProfile] = useState(null);
  const [editLoc, setEditLoc] = useState(null); // null | location object being edited
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/company-profile");
      setProfile(data);
    } catch {
      toast.error("Could not load company profile");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveProfile = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/company-profile", patch);
      setProfile(data);
      toast.success("Company settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save company settings");
    } finally {
      setBusy(false);
    }
  };

  const saveLocation = async () => {
    if (!editLoc.description.trim()) { toast.error("Description is required"); return; }
    const locs = [...(profile.locations || [])];
    const loc = { ...editLoc, location_id: editLoc.location_id || `loc_${Date.now()}` };
    if (loc.primary) locs.forEach((l) => { l.primary = false; });
    const idx = locs.findIndex((l) => l.location_id === loc.location_id);
    if (idx >= 0) locs[idx] = loc; else locs.push(loc);
    if (!locs.some((l) => l.primary)) locs[0].primary = true;
    await saveProfile({ locations: locs });
    setEditLoc(null);
  };

  const deleteLocation = async (loc) => {
    if (!window.confirm(`Delete location "${loc.description}"?`)) return;
    let locs = (profile.locations || []).filter((l) => l.location_id !== loc.location_id);
    if (locs.length && !locs.some((l) => l.primary)) locs = [{ ...locs[0], primary: true }, ...locs.slice(1)];
    await saveProfile({ locations: locs });
  };

  if (!profile) {
    return <p className="py-6 text-center font-mono text-xs uppercase tracking-widest text-white/40">Loading company…</p>;
  }

  const setP = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="space-y-6" data-testid="company-panel">
      {/* ------------------------- LOCATIONS ------------------------- */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-amber-400" />
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">Locations</p>
          </div>
          <button onClick={() => setEditLoc({ ...emptyLoc })} data-testid="add-location-btn"
            className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white hover:bg-amber-500">
            <Plus className="h-3 w-3" /> Add Location
          </button>
        </div>
        <p className="mb-2 text-xs text-white/40">
          When an employee's work location is <span className="text-white/70">Office</span>, their payroll profile
          shows the primary location's address from here.
        </p>
        <div className="overflow-hidden rounded-lg border border-white/10">
          <table className="w-full text-left text-xs" data-testid="locations-table">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.03] font-mono text-[9px] uppercase tracking-widest text-white/40">
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Address</th>
                <th className="px-3 py-2">City</th>
                <th className="px-3 py-2">Province</th>
                <th className="px-3 py-2">Postal Code</th>
                <th className="px-3 py-2">Phone Number</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {(profile.locations || []).map((l) => (
                <tr key={l.location_id} className="border-b border-white/5 last:border-0" data-testid={`location-row-${l.location_id}`}>
                  <td className="px-3 py-2 text-white/90">
                    {l.description}
                    {l.primary && <span className="ml-1 font-mono text-[8px] uppercase tracking-widest text-white/40">(Primary)</span>}
                  </td>
                  <td className="px-3 py-2 text-white/70">{l.address}</td>
                  <td className="px-3 py-2 text-white/70">{l.city}</td>
                  <td className="px-3 py-2 text-white/70">{l.province}</td>
                  <td className="px-3 py-2 font-mono text-white/70">{l.postal}</td>
                  <td className="px-3 py-2 font-mono text-white/70">{l.phone}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditLoc({ ...l })} aria-label="Edit location" data-testid={`location-edit-${l.location_id}`}
                      className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => deleteLocation(l)} aria-label="Delete location" data-testid={`location-delete-${l.location_id}`}
                      className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
              {!(profile.locations || []).length && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-white/30">No locations yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ------------------------- COMPANY DETAILS ------------------------- */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-[#8fa4f0]" />
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">Company details &amp; ROE contact</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs text-white/50">Legal name</Label>
            <Input value={profile.legal_name || ""} onChange={setP("legal_name")} data-testid="company-legal-name" className={inputCls} /></div>
          <div><Label className="text-xs text-white/50">Trade / operating name (on invoices &amp; letters)</Label>
            <Input value={profile.trade_name || ""} onChange={setP("trade_name")} data-testid="company-trade-name" className={inputCls} /></div>
          <div><Label className="text-xs text-white/50">GST/HST registration no.</Label>
            <Input value={profile.hst_number || ""} onChange={setP("hst_number")} data-testid="company-hst-number" className={`${inputCls} font-mono`} /></div>
          <div className="col-span-2"><Label className="text-xs text-white/50">Company email (invoices &amp; reminders)</Label>
            <Input type="email" value={profile.email || ""} onChange={setP("email")} data-testid="company-email" className={inputCls} /></div>
          <div className="col-span-2"><Label className="text-xs text-white/50">Address line 1</Label>
            <Input value={profile.address_line1 || ""} onChange={setP("address_line1")} data-testid="company-address" className={inputCls} /></div>
          <div><Label className="text-xs text-white/50">City</Label>
            <Input value={profile.city || ""} onChange={setP("city")} data-testid="company-city" className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-white/50">Province</Label>
              <select value={profile.province || ""} onChange={setP("province")} data-testid="company-province"
                className="mt-1 h-9 w-full rounded-md border border-white/15 bg-white/5 px-2 text-sm text-white">
                <option value="">—</option>
                {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select></div>
            <div><Label className="text-xs text-white/50">Postal code</Label>
              <Input value={profile.postal || ""} onChange={setP("postal")} data-testid="company-postal" className={`${inputCls} font-mono`} /></div>
          </div>
          <div><Label className="text-xs text-white/50">CRA business number (BN)</Label>
            <Input value={profile.cra_bn || ""} onChange={setP("cra_bn")} data-testid="company-cra-bn" className={`${inputCls} font-mono`}  /></div>
          <div><Label className="text-xs text-white/50">Payroll reference number</Label>
            <Input value={profile.payroll_ref || ""} onChange={setP("payroll_ref")} data-testid="company-payroll-ref" className={inputCls} /></div>
          <div><Label className="text-xs text-white/50">ROE contact first name</Label>
            <Input value={profile.contact_first || ""} onChange={setP("contact_first")} data-testid="company-contact-first" className={inputCls} /></div>
          <div><Label className="text-xs text-white/50">ROE contact last name</Label>
            <Input value={profile.contact_last || ""} onChange={setP("contact_last")} data-testid="company-contact-last" className={inputCls} /></div>
          <div><Label className="text-xs text-white/50">Language</Label>
            <select value={profile.language || "English"} onChange={setP("language")} data-testid="company-language"
              className="mt-1 h-9 w-full rounded-md border border-white/15 bg-white/5 px-2 text-sm text-white">
              {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs text-white/50">Area code</Label>
              <Input value={profile.area_code || ""} onChange={setP("area_code")} data-testid="company-area-code" className={inputCls} /></div>
            <div><Label className="text-xs text-white/50">Phone</Label>
              <Input value={profile.phone || ""} onChange={setP("phone")} data-testid="company-phone" className={inputCls} /></div>
            <div><Label className="text-xs text-white/50">Ext.</Label>
              <Input value={profile.extension || ""} onChange={setP("extension")} data-testid="company-extension" className={inputCls} /></div>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={() => saveProfile(profile)} disabled={busy} data-testid="company-save-btn"
            className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85">
            {busy ? "Saving…" : "Save company details"}
          </Button>
        </div>
      </div>

      {/* ------------------------- location editor modal ------------------------- */}
      {editLoc && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="location-editor">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditLoc(null)} />
          <div className="relative w-full max-w-md rounded-xl border border-white/10 bg-[#0d0d0d] p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold text-white">{editLoc.location_id ? "Edit Location" : "Add Location"}</h3>
              <button onClick={() => setEditLoc(null)} aria-label="Close" className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <div><Label className="text-xs text-white/50">*Description</Label>
                <Input value={editLoc.description} onChange={(e) => setEditLoc((p) => ({ ...p, description: e.target.value }))}
                  data-testid="location-description" className={inputCls} placeholder="Office" /></div>
              <div><Label className="text-xs text-white/50">Address</Label>
                <Input value={editLoc.address} onChange={(e) => setEditLoc((p) => ({ ...p, address: e.target.value }))}
                  data-testid="location-address" className={inputCls}  /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs text-white/50">City</Label>
                  <Input value={editLoc.city} onChange={(e) => setEditLoc((p) => ({ ...p, city: e.target.value }))}
                    data-testid="location-city" className={inputCls} /></div>
                <div><Label className="text-xs text-white/50">Province</Label>
                  <select value={editLoc.province} onChange={(e) => setEditLoc((p) => ({ ...p, province: e.target.value }))}
                    data-testid="location-province"
                    className="mt-1 h-9 w-full rounded-md border border-white/15 bg-white/5 px-2 text-sm text-white">
                    {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select></div>
                <div><Label className="text-xs text-white/50">Postal code</Label>
                  <Input value={editLoc.postal} onChange={(e) => setEditLoc((p) => ({ ...p, postal: e.target.value }))}
                    data-testid="location-postal" className={`${inputCls} font-mono`} /></div>
                <div><Label className="text-xs text-white/50">Phone number</Label>
                  <Input value={editLoc.phone} onChange={(e) => setEditLoc((p) => ({ ...p, phone: e.target.value }))}
                    data-testid="location-phone" className={`${inputCls} font-mono`} /></div>
              </div>
              <label className="flex items-center gap-2 text-xs text-white/70">
                <input type="checkbox" checked={!!editLoc.primary} data-testid="location-primary"
                  onChange={(e) => setEditLoc((p) => ({ ...p, primary: e.target.checked }))}
                  className="h-3.5 w-3.5 accent-amber-500" />
                Primary location (used for "Office" work-location addresses)
              </label>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={saveLocation} disabled={busy} data-testid="location-save-btn"
                className="bg-amber-600 text-white hover:bg-amber-500">Save Changes</Button>
              <Button onClick={() => setEditLoc(null)} variant="outline"
                className="border-white/20 bg-transparent text-white hover:bg-white/10">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
